import { styled } from "@/src/components/templates/Users/UsersOverviewTemplate.styles";
import { useTranslation } from "react-i18next";
import { memo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
    Operator,
    useGetUsersQuery,
    UsersFieldSelector,
} from "@/src/graphql/generated/api/graphql";
import UsersTable from "@/src/components/organisms/UsersTable/UsersTable";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { usePermissions } from "@/src/lib/usePermissions/usePermissions";

type UsersFilter = "active" | "deactivated";

const UsersOverviewTemplate = () => {
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(15);
    const [showActivated, setShowActivated] = useState(true);
    const [filter, setFilter] = useState<UsersFilter>("active");

    const { loading, data } = useGetUsersQuery({
        fetchPolicy: "network-only",
        variables: {
            filters: {
                selector: UsersFieldSelector.Enabled,
                operator: Operator.Equals,
                value: showActivated.toString(),
            },
            page,
            pageSize,
        },
    });

    const users = data?.getUsers.rows.map((user) => ({
        id: user.id,
        name: `${user.firstName} ${user.lastName}`,
        email: user.email,
    }));

    const pagination = data?.getUsers.pagination;

    const navigate = useNavigate();

    const { t } = useTranslation("common", {
        keyPrefix: "users-overview",
    });

    const handleUserClick = (userId: string) => {
        navigate({ to: `/admin/users/${userId}` });
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setPageSize(parseInt(event.target.value));
        setPage(0);
    };

    const handleChangeUserFilter = (newFilter: UsersFilter) => {
        setFilter(newFilter);
        setShowActivated(newFilter === "active");
        setPage(0);
    };

    const { hasPermission } = usePermissions();

    return (
        <>
            <styled.Container>
                <styled.Header
                    title={t("title")}
                    {...(hasPermission("USERS_CREATE") && {
                        button: {
                            to: "new",
                            label: t("new"),
                        },
                    })}
                />
                <styled.Main>
                    <FormControl sx={{ mb: 4 }}>
                        <InputLabel id="filter-users-label">
                            {t("show")}
                        </InputLabel>
                        <Select
                            labelId="filter-users-label"
                            label={t("show")}
                            value={filter}
                            onChange={(event) => {
                                handleChangeUserFilter(
                                    event.target.value as UsersFilter
                                );
                            }}
                        >
                            <MenuItem value="active">
                                {t("active-users")}
                            </MenuItem>
                            <MenuItem value="deactivated">
                                {t("deactivated-users")}
                            </MenuItem>
                        </Select>
                    </FormControl>
                    <UsersTable
                        users={users || []}
                        loading={loading}
                        onUserClick={handleUserClick}
                        page={page}
                        rowsPerPage={pageSize}
                        totalCount={pagination?.totalCount || 0}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </styled.Main>
            </styled.Container>
        </>
    );
};

export default memo(UsersOverviewTemplate);
